// Ben's "Top leagues" (2026-10-16): the leagues people use most, pinned first in the League chip's
// list, in this order; everything else follows grouped by country. Ben decides this list — edit it
// here. Each entry matches the DB's own name (any of `names`, ignoring case and a " (Country)"
// suffix the picker adds to shared names), its country (null = international) and gender.

export interface TopLeague {
  womens: boolean;
  country: string | null;
  names: readonly string[];
}

export const TOP_LEAGUES: readonly TopLeague[] = [
  // Men's
  { womens: false, country: "England", names: ["Premier League"] },
  { womens: false, country: "Spain", names: ["La Liga", "LaLiga", "Primera División", "Primera Division"] },
  { womens: false, country: "Italy", names: ["Serie A"] },
  { womens: false, country: "Germany", names: ["1. Bundesliga", "Bundesliga"] },
  { womens: false, country: "France", names: ["Ligue 1"] },
  { womens: false, country: null, names: ["Champions League", "UEFA Champions League"] },
  { womens: false, country: null, names: ["Europa League", "UEFA Europa League"] },
  { womens: false, country: "Netherlands", names: ["Eredivisie"] },
  { womens: false, country: "Portugal", names: ["Primeira Liga", "Liga Portugal", "Liga NOS"] },
  { womens: false, country: "United States of America", names: ["Major League Soccer", "MLS"] },
  { womens: false, country: "England", names: ["Championship"] },
  // Women's
  { womens: true, country: "England", names: ["FAWSL", "FA Women's Super League", "Women's Super League", "WSL"] },
  { womens: true, country: "United States of America", names: ["NWSL", "National Women's Soccer League"] },
  { womens: true, country: "Spain", names: ["Liga F", "Primera División Femenina", "Primera Division Femenina"] },
  { womens: true, country: "Germany", names: ["Frauen-Bundesliga", "Frauen Bundesliga", "Women's Bundesliga"] },
  { womens: true, country: "France", names: ["Première Ligue", "Premiere Ligue", "D1 Arkema", "Division 1 Féminine", "Division 1 Feminine"] },
  { womens: true, country: "Italy", names: ["Serie A Femminile", "Serie A Women", "Women's Serie A"] },
  { womens: true, country: null, names: ["Women's Champions League", "UEFA Women's Champions League", "UWCL"] },
  { womens: true, country: "England", names: ["FA Women's Championship", "Women's Championship", "Championship"] },
];

const norm = (s: string) => s.replace(/\s*\([^)]*\)\s*$/, "").trim().toLowerCase();
// The DB names the US variously; any of these is "the US".
const US = new Set(["united states of america", "united states", "usa"]);
const sameCountry = (a: string | null, b: string | null) =>
  a === null || b === null ? a === b : a.toLowerCase() === b.toLowerCase() || (US.has(a.toLowerCase()) && US.has(b.toLowerCase()));

export interface LeagueForTop {
  label: string;
  countryName: string | null;
  womens: boolean | null;
}

/**
 * The top leagues found among `leagues`, in Ben's order (their picker labels). A top league not
 * found is listed in the console, so a name can be corrected here. Pure apart from that note.
 */
export function topLeagueLabels(leagues: readonly LeagueForTop[]): string[] {
  const out: string[] = [];
  const missing: string[] = [];
  const genders = new Set(leagues.map((l) => l.womens));
  for (const t of TOP_LEAGUES) {
    if (!genders.has(t.womens)) continue; // only the list's own gender is offered
    const hit = leagues.find(
      (l) => l.womens === t.womens && sameCountry(l.countryName, t.country) && t.names.some((n) => norm(l.label) === n.toLowerCase()),
    );
    if (hit && !out.includes(hit.label)) out.push(hit.label);
    else if (!hit) missing.push(`${t.names[0]} (${t.country ?? "International"})`);
  }
  // Once per name per page: lists redraw often, and a top league with no data that season is
  // left out of the League chip's list on purpose (only leagues with data, #303).
  const fresh = missing.filter((m) => !noted.has(m));
  for (const m of fresh) noted.add(m);
  if (fresh.length > 0) console.info(`[top leagues] not in this list (no data, or a name to correct here): ${fresh.join(", ")}`);
  return out;
}

const noted = new Set<string>();

/**
 * Where a league sits in Ben's list for its own gender (0 = first), or null when it isn't a top
 * league. Per gender, so the Premier League and the WSL both count as first: the player search
 * (live/player-index.ts) puts players from a top league first, a men's and a women's list side
 * by side. Pure.
 */
export function topLeagueRank(league: { name: string; countryName: string | null; womens: boolean | null }): number | null {
  if (league.womens === null) return null;
  const own = TOP_LEAGUES.filter((t) => t.womens === league.womens);
  const i = own.findIndex((t) => sameCountry(league.countryName, t.country) && t.names.some((n) => norm(league.name) === n.toLowerCase()));
  return i === -1 ? null : i;
}
